import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  useColorModeValue,
  VStack,
  HStack,
  Badge,
  Icon,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  IoMdCheckmarkCircleOutline,
  IoMdCloseCircleOutline,
} from "react-icons/io";
import {
  FaBuilding,
  FaUserFriends,
  FaMobileAlt,
  FaPrint,
  FaHospital,
  FaShieldAlt,
} from "react-icons/fa";
import type { IconType } from "react-icons";

import Footer from "../Common/Footer";
import Header from "../Common/Header";
import { handleCTA } from "../../utils";

// Motion components
const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);
const MotionButton = motion(Button);
const MotionHStack = motion(HStack);

// Animations
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// Define Feature interface
interface Feature {
  text: string;
  included: boolean;
  icon?: IconType;
  highlight?: boolean;
}

// Pricing Data Structure
const PRICING_PLANS = [
  {
    name: "Lite",
    price: "$9.8",
    frequency: "/month",
    description:
      "Perfect for individual healthcare professionals starting their AI journey.",
    credits: "100 credits",
    minutes: "200 mins clinical data",
    features: [
      { text: "AI-Powered Transcriptions", included: true, highlight: true },
      { text: "Basic Medical Insights", included: true },
      { text: "Web Application Access", included: true },
      { text: "HIPAA Compliant", included: true, highlight: true },
      { text: "Report Sharing & Extraction", included: false },
      { text: "Collaboration Tools", included: false },
      { text: "Basic Quality AI Models", included: true },
      { text: "Email Support", included: true },
    ] as Feature[],
    ctaText: "Start with Lite",
    ctaAction: () => handleCTA("login"),
    color: "#10B981",
    bgColor: "#10B98115",
  },
  {
    name: "Flexible",
    price: "$24",
    frequency: "/month",
    description:
      "For growing professionals who need more power and flexibility.",
    credits: "200 credits",
    minutes: "400 mins clinical data",
    features: [
      { text: "AI-Powered Transcriptions", included: true, highlight: true },
      { text: "Enhanced Medical Insights", included: true, highlight: true },
      { text: "Web & Mobile App Access", included: true, icon: FaMobileAlt },
      { text: "HIPAA Compliant", included: true, highlight: true },
      { text: "Report Sharing & Extraction", included: false },
      { text: "Collaboration Tools", included: false },
      { text: "Advanced Quality AI Models", included: true },
      { text: "Bring Your Own Key (BYOK)", included: true, highlight: true },
      { text: "Priority Email Support", included: true },
    ] as Feature[],
    ctaText: "Choose Flexible",
    ctaAction: () => handleCTA("login"),
    isPopular: true,
    color: "#6366F1",
    bgColor: "#6366F115",
  },
  {
    name: "Premium",
    price: "$200",
    frequency: "/user/month",
    description: "Complete solution for healthcare teams and clinics.",
    credits: "Unlimited",
    minutes: "Unlimited clinical data",
    features: [
      { text: "AI-Powered Transcriptions", included: true, highlight: true },
      { text: "Advanced Medical Insights", included: true, highlight: true },
      { text: "Web & Mobile App Access", included: true, icon: FaMobileAlt },
      { text: "HIPAA Compliant", included: true, highlight: true },
      { text: "Report Sharing & Extraction", included: true, icon: FaPrint },
      { text: "Collaboration & Sharing", included: true, icon: FaUserFriends },
      { text: "Premium Quality AI Models", included: true, highlight: true },
      { text: "Bring Your Own Key (BYOK)", included: true, highlight: true },
      { text: "EHR Integration", included: true, highlight: true },
      { text: "Medical Coding Assistance", included: true, highlight: true },
      { text: "Multi-Tenancy Options", included: true },
      { text: "Dedicated Support", included: true },
    ] as Feature[],
    ctaText: "Go Premium",
    ctaAction: () => handleCTA("login"),
    color: "#8B5CF6",
    bgColor: "#8B5CF615",
  },
  {
    name: "Enterprise",
    price: "Custom",
    frequency: "",
    description: "Tailored solutions for large healthcare organizations.",
    credits: "Unlimited",
    minutes: "Unlimited clinical data",
    features: [
      { text: "All Premium Features", included: true, highlight: true },
      { text: "Custom AI Model Training", included: true, highlight: true },
      { text: "Dedicated Support Team", included: true },
      { text: "Custom Integrations", included: true, highlight: true },
      {
        text: "Advanced Security & Compliance",
        included: true,
        highlight: true,
      },
      { text: "Volume Discounts", included: true },
      { text: "Full Multi-Tenancy", included: true, icon: FaBuilding },
      { text: "SLA Guarantees", included: true },
      { text: "On-Premise Options", included: true, highlight: true },
    ] as Feature[],
    ctaText: "Contact Sales",
    ctaAction: () => handleCTA("login"),
    color: "#F59E0B",
    bgColor: "#F59E0B15",
  },
];

// FAQ Data
const FAQS = [
  {
    question: "How do credits and minutes work?",
    answer:
      "Each plan comes with a monthly allowance of credits or clinical data minutes. Generating summaries or using certain AI features consumes credits/minutes. 1 credit typically corresponds to approximately 2 minutes of clinical data processing, but this can vary based on complexity.",
  },
  {
    question: "Do unused credits/minutes carry over?",
    answer:
      "Credits and minutes are renewed monthly based on your plan and do not roll over to the next billing cycle. Unused allowances expire at the end of the month.",
  },
  {
    question: "What is Bring Your Own Key (BYOK)?",
    answer:
      "BYOK allows organizations to use their own encryption keys for enhanced data security and control, available on Flexible plans and higher. This provides additional security layers for healthcare organizations with strict compliance requirements.",
  },
  {
    question: "Can I change my plan later?",
    answer:
      "Yes, you can upgrade or downgrade your plan at any time through your account settings. Changes will typically take effect at the start of your next billing cycle.",
  },
  {
    question: "How does EHR Integration work?",
    answer:
      "Our Premium and Enterprise plans offer integration capabilities with major EHR systems (like Epic, Cerner). This allows for seamless data flow between our platform and your existing records. Contact sales for specific integration details.",
  },
  {
    question: "What security measures are in place?",
    answer:
      "All plans include HIPAA compliance, end-to-end encryption, and enterprise-grade security. Premium and Enterprise plans offer additional security features including BYOK, custom integrations, and on-premise deployment options.",
  },
];

function PricingPageV2() {
  const cardBg = useColorModeValue("white", "gray.800");
  const sectionBg = useColorModeValue("gray.50", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textPrimary = useColorModeValue("gray.900", "white");
  const textSecondary = useColorModeValue("gray.600", "gray.300");
  const purplePrimary = "#6366F1";
  const greenSuccess = "#10B981";

  const blueInfo = "#3B82F6";

  return (
    <Box>
      <Header minimal={false} />

      {/* Hero Section */}
      <Box
        bg={sectionBg}
        py={{ base: 16, md: 24 }}
        position="relative"
        overflow="hidden"
      >
        {/* Background Pattern */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.03}
          backgroundImage={`url("data:image/svg+xml,%3csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3e%3cg fill='none' fill-rule='evenodd'%3e%3cg fill='%236366F1' fill-opacity='0.1'%3e%3ccircle cx='30' cy='30' r='2'/%3e%3c/g%3e%3c/g%3e%3c/svg%3e")`}
          backgroundSize="60px 60px"
        />

        <Container
          maxW="container.lg"
          textAlign="center"
          position="relative"
          zIndex={1}
        >
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Badge
              px={4}
              py={2}
              bg={`${purplePrimary}15`}
              color={purplePrimary}
              fontSize="sm"
              fontWeight="600"
              borderRadius="full"
              letterSpacing="wide"
              textTransform="uppercase"
              border={`1px solid ${purplePrimary}30`}
              mb={6}
            >
              Healthcare-First Pricing
            </Badge>
          </MotionBox>

          <MotionHeading
            as="h1"
            size="2xl"
            mb={6}
            fontWeight="700"
            color={textPrimary}
            fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
            lineHeight="1.2"
            {...fadeInUp}
          >
            Choose the Right Plan for Your Practice
          </MotionHeading>

          <MotionText
            fontSize={{ base: "lg", md: "xl" }}
            color={textSecondary}
            maxW="container.md"
            mx="auto"
            lineHeight="1.6"
            {...fadeInUp}
            transition={{ delay: 0.2 }}
          >
            Start with what you need, scale as you grow. Transparent,
            healthcare-focused pricing designed for medical professionals.
          </MotionText>

          {/* Trust Indicators */}
          <MotionHStack
            spacing={8}
            justify="center"
            mt={8}
            {...fadeInUp}
            transition={{ delay: 0.3 }}
          >
            <VStack spacing={1}>
              <Icon as={FaShieldAlt} boxSize={6} color={greenSuccess} />
              <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                HIPAA Compliant
              </Text>
            </VStack>
            <VStack spacing={1}>
              <Icon
                as={IoMdCheckmarkCircleOutline}
                boxSize={6}
                color={purplePrimary}
              />
              <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                99.8% Accuracy
              </Text>
            </VStack>
            <VStack spacing={1}>
              <Icon as={FaHospital} boxSize={6} color={blueInfo} />
              <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                500+ Hospitals
              </Text>
            </VStack>
          </MotionHStack>
        </Container>
      </Box>

      {/* Pricing Grid */}
      <Container maxW="container.2xl" py={20} px={{ base: 4, md: 8 }}>
        <MotionBox
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 4 }}
            spacing={{ base: 8, lg: 6 }}
            alignItems="stretch"
          >
            {PRICING_PLANS.map((plan) => (
              <MotionBox
                key={plan.name}
                variants={fadeInUp}
                whileHover={{
                  y: -8,
                  transition: { duration: 0.3 },
                }}
              >
                <Box
                  bg={cardBg}
                  borderRadius="3xl"
                  boxShadow="0 8px 32px rgba(0, 0, 0, 0.08)"
                  p={8}
                  borderWidth={plan.isPopular ? 3 : 1}
                  borderColor={plan.isPopular ? plan.color : borderColor}
                  position="relative"
                  display="flex"
                  flexDirection="column"
                  transition="all 0.3s ease"
                  _hover={{
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.12)",
                    borderColor: plan.color,
                  }}
                  overflow="hidden"
                >
                  {/* Background accent */}
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    height="4px"
                    bgGradient={`linear(to-r, ${plan.color}, ${plan.color}80)`}
                  />

                  {plan.isPopular && (
                    <Badge
                      position="absolute"
                      top={4}
                      right={4}
                      bg={plan.color}
                      color="white"
                      px={3}
                      py={1}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="bold"
                      boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
                    >
                      Most Popular
                    </Badge>
                  )}

                  <VStack spacing={6} align="stretch" flexGrow={1}>
                    <VStack spacing={4}>
                      <Heading
                        as="h3"
                        size="lg"
                        textAlign="center"
                        color={textPrimary}
                        fontWeight="700"
                      >
                        {plan.name}
                      </Heading>

                      <HStack justify="center" align="baseline">
                        <Text
                          fontSize="4xl"
                          fontWeight="bold"
                          color={plan.color}
                        >
                          {plan.price}
                        </Text>
                        {plan.frequency && (
                          <Text color={textSecondary} fontSize="lg">
                            {plan.frequency}
                          </Text>
                        )}
                      </HStack>

                      <Text
                        textAlign="center"
                        fontSize="md"
                        color={textSecondary}
                        lineHeight="1.6"
                        minH="60px"
                      >
                        {plan.description}
                      </Text>
                    </VStack>

                    <Divider borderColor={borderColor} />

                    <VStack spacing={3} align="center">
                      <Text fontWeight="700" fontSize="lg" color={textPrimary}>
                        {plan.credits}
                      </Text>
                      {plan.minutes && (
                        <Text fontSize="sm" color={textSecondary}>
                          or {plan.minutes}
                        </Text>
                      )}
                    </VStack>

                    <List spacing={4} flexGrow={1}>
                      {plan.features.map((feature, featureIdx) => (
                        <ListItem
                          key={featureIdx}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          bg={
                            feature.highlight
                              ? `${plan.color}08`
                              : "transparent"
                          }
                          p={feature.highlight ? 3 : 2}
                          borderRadius="md"
                          minH="40px"
                        >
                          <HStack spacing={3} flex="1">
                            <ListIcon
                              as={
                                feature.included
                                  ? IoMdCheckmarkCircleOutline
                                  : IoMdCloseCircleOutline
                              }
                              color={feature.included ? plan.color : "red.400"}
                              boxSize={5}
                              flexShrink={0}
                            />
                            <Text
                              fontSize="sm"
                              fontWeight={feature.highlight ? "600" : "400"}
                              color={textPrimary}
                              flex="1"
                            >
                              {feature.text}
                            </Text>
                          </HStack>
                          {feature.icon && (
                            <Icon
                              as={feature.icon}
                              color={textSecondary}
                              boxSize={4}
                              flexShrink={0}
                            />
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </VStack>

                  <MotionButton
                    mt={8}
                    w="full"
                    bg={plan.isPopular ? plan.color : "transparent"}
                    color={plan.isPopular ? "white" : plan.color}
                    border={`2px solid ${plan.color}`}
                    variant={plan.isPopular ? "solid" : "outline"}
                    onClick={plan.ctaAction}
                    borderRadius="xl"
                    py={6}
                    fontWeight="700"
                    fontSize="lg"
                    _hover={{
                      bg: plan.isPopular ? `${plan.color}dd` : plan.color,
                      color: "white",
                      transform: "translateY(-2px)",
                      boxShadow: `0 10px 25px ${plan.color}30`,
                    }}
                    _active={{
                      transform: "translateY(0)",
                    }}
                    transition="all 0.3s ease"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {plan.ctaText}
                  </MotionButton>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>
        </MotionBox>
      </Container>

      {/* FAQ Section */}
      <Box bg={sectionBg} py={20} position="relative" overflow="hidden">
        {/* Background Pattern */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.03}
          backgroundImage={`url("data:image/svg+xml,%3csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3e%3cg fill='none' fill-rule='evenodd'%3e%3cg fill='%236366F1' fill-opacity='0.1'%3e%3ccircle cx='30' cy='30' r='2'/%3e%3c/g%3e%3c/g%3e%3c/svg%3e")`}
          backgroundSize="60px 60px"
        />

        <Container maxW="container.lg" position="relative" zIndex={1}>
          <MotionBox textAlign="center" mb={12} {...fadeInUp}>
            <Heading
              as="h2"
              size="xl"
              color={textPrimary}
              fontWeight="700"
              mb={4}
            >
              Frequently Asked Questions
            </Heading>
            <Text
              fontSize="lg"
              color={textSecondary}
              maxW="container.md"
              mx="auto"
            >
              Everything you need to know about our healthcare AI solutions
            </Text>
          </MotionBox>

          <Accordion allowMultiple>
            {FAQS.map((faq, idx) => (
              <MotionBox
                key={idx}
                mb={4}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
              >
                <AccordionItem border="none">
                  {({ isExpanded }) => (
                    <>
                      <AccordionButton
                        bg={cardBg}
                        borderRadius="xl"
                        p={6}
                        _hover={{
                          bg: useColorModeValue("gray.100", "gray.700"),
                          transform: "translateY(-2px)",
                        }}
                        boxShadow={
                          isExpanded
                            ? "0 8px 32px rgba(0, 0, 0, 0.08)"
                            : "0 4px 16px rgba(0, 0, 0, 0.04)"
                        }
                        transition="all 0.3s ease"
                        border={`1px solid ${borderColor}`}
                      >
                        <Box
                          flex="1"
                          textAlign="left"
                          fontWeight="600"
                          color={textPrimary}
                        >
                          {faq.question}
                        </Box>
                        <AccordionIcon color={purplePrimary} />
                      </AccordionButton>
                      <AccordionPanel
                        pb={6}
                        pt={4}
                        px={6}
                        bg={cardBg}
                        borderBottomRadius="xl"
                        boxShadow="0 4px 16px rgba(0, 0, 0, 0.04)"
                        border={`1px solid ${borderColor}`}
                        borderTop="none"
                      >
                        <Text color={textSecondary} lineHeight="1.6">
                          {faq.answer}
                        </Text>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              </MotionBox>
            ))}
          </Accordion>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}

export default PricingPageV2;
